import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';

/**
 * CookieConsentBanner
 * -------------------
 * Sticky bottom banner shown until the user makes a choice. Three actions:
 *  · Accept all                (analytics + marketing ON)
 *  · Reject non-essential      (analytics + marketing OFF, only strictly-
 *                               necessary session cookies remain)
 *  · Manage preferences        (per-category toggles)
 *
 * Choice is persisted in localStorage under `hh_cookie_consent`. Firing
 * a `cookie-consent-changed` window event lets analytics scripts (GA, Meta
 * Pixel, etc.) attach/detach at runtime once they're wired in.
 */
const STORAGE_KEY = 'hh_cookie_consent';

export const getConsent = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
  catch { return null; }
};

const CookieConsentBanner = () => {
  const [choiceMade, setChoiceMade] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    setChoiceMade(!!getConsent());
  }, []);

  const persist = (payload) => {
    const record = { ...payload, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: record }));
    setChoiceMade(true);
  };

  const acceptAll = () => persist({ status: 'accepted', analytics: true, marketing: true });
  const rejectNonEssential = () => persist({ status: 'rejected', analytics: false, marketing: false });
  const savePrefs = () => persist({ status: 'partial', ...prefs });

  if (choiceMade) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-slate-300 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <div className="hidden sm:flex items-center justify-center h-10 w-10 rounded-full bg-red-50 text-red-700 shrink-0">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900 mb-1">We use cookies</p>
            <p className="text-xs text-slate-600 leading-relaxed">
              We use strictly-necessary cookies to run this site (login,
              billing, security). With your consent, we also use analytics
              cookies to improve the product. We do <strong>not</strong> sell
              your data.{' '}
              <a
                href="/privacy"
                className="underline text-red-700 hover:text-red-900"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </p>

            {showPrefs && (
              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" data-testid="cookie-consent-prefs">
                <label className="flex items-center justify-between gap-3 text-xs">
                  <span>
                    <strong className="text-slate-900">Strictly necessary</strong>
                    <span className="block text-slate-500">Required — cannot be disabled.</span>
                  </span>
                  <input type="checkbox" checked disabled className="accent-red-600" />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs">
                  <span>
                    <strong className="text-slate-900">Analytics</strong>
                    <span className="block text-slate-500">Helps us understand which features get used.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    className="accent-red-600"
                    data-testid="cookie-consent-analytics"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs">
                  <span>
                    <strong className="text-slate-900">Marketing</strong>
                    <span className="block text-slate-500">Personalizes messages if you leave and come back.</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                    className="accent-red-600"
                    data-testid="cookie-consent-marketing"
                  />
                </label>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {showPrefs ? (
                <>
                  <button
                    onClick={savePrefs}
                    className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2"
                    data-testid="cookie-consent-save-prefs"
                  >
                    Save my choices
                  </button>
                  <button
                    onClick={() => setShowPrefs(false)}
                    className="text-xs font-semibold text-slate-700 hover:text-slate-900 rounded-full px-3 py-2"
                  >
                    Back
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={acceptAll}
                    className="text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2"
                    data-testid="cookie-consent-accept"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={rejectNonEssential}
                    className="text-xs font-semibold bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-full px-4 py-2"
                    data-testid="cookie-consent-reject"
                  >
                    Reject non-essential
                  </button>
                  <button
                    onClick={() => setShowPrefs(true)}
                    className="text-xs font-semibold text-slate-700 hover:text-slate-900 rounded-full px-3 py-2 underline underline-offset-2"
                    data-testid="cookie-consent-manage"
                  >
                    Manage preferences
                  </button>
                </>
              )}
            </div>
          </div>
          <button
            onClick={rejectNonEssential}
            aria-label="Reject non-essential and close"
            className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="cookie-consent-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
